import { BitkitButton, BitkitDialog, BitkitList } from '@bitrise/bitkit-v2';
import { useDisclosure } from '@chakra-ui/react/hooks';
import { Text } from '@chakra-ui/react/text';

import useCreditSubscription from '@/hooks/api/useCreditSubscription';
import { useMutateUserMetadata } from '@/hooks/api/useUserMetadata';
import { Account } from '@/models/Account';
import { exceededLimitsPath } from '@/utils/monolithApiRouteService';

const PlanDowngradeDialog = ({ account }: { account: Account }) => {
  const { open, onClose } = useDisclosure({ defaultOpen: true });

  const shouldShowDialog = Boolean(account?.downgradeNotifications?.dialog);

  const { data: subscription } = useCreditSubscription(account, { enabled: shouldShowDialog });
  const limits = subscription?.exceededScheduledLimits;

  const { mutate } = useMutateUserMetadata({ transform: false });

  if (!account || !shouldShowDialog || !limits) {
    return null;
  }

  const onDialogClose = () => {
    mutate({ [`scheduled_downgrade_dialog_notification_on_${account.slug}`]: false });
    onClose();
  };

  return (
    <BitkitDialog
      open={open}
      onOpenChange={({ open: isOpen }) => !isOpen && onDialogClose()}
      title="Your plan change will be effective in 5 days"
      footerButtons={<BitkitButton href={exceededLimitsPath(account.slug)}>Manage exceeded limits</BitkitButton>}
    >
      <Text>
        By switching to the new plan, you will exceed some of its limits. To continue using Bitrise after, please review
        the changes you need to make:
      </Text>
      <BitkitList variant="unstyled">
        {limits.owners && (
          <BitkitList.Item>
            <span>
              <strong>Owner:</strong> {limits.owners.count} (of {limits.owners.limit})
            </span>
          </BitkitList.Item>
        )}
        {limits.teamMembers && (
          <BitkitList.Item>
            <span>
              <strong>Team member:</strong> {limits.teamMembers.count} (of {limits.teamMembers.limit})
            </span>
          </BitkitList.Item>
        )}
        {limits.uniqueStepsExceeded && (
          <BitkitList.Item>
            <span>
              <strong>Steps per project limit</strong> (in {limits.uniqueStepsExceeded}{' '}
              {limits.uniqueStepsExceeded > 1 ? 'projects' : 'project'})
            </span>
          </BitkitList.Item>
        )}
        {limits.privateApps && (
          <BitkitList.Item>
            <span>
              <strong>Private projects:</strong> {limits.privateApps.count} (of {limits.privateApps.limit})
            </span>
          </BitkitList.Item>
        )}
      </BitkitList>
    </BitkitDialog>
  );
};

export default PlanDowngradeDialog;
